import { addBusinessDaysFromStart } from './taskSchedule';

export function isTaskReminderInFuture(remindAt: Date, now: Date = new Date()): boolean {
  return remindAt.getTime() > now.getTime();
}

export function assertSelfTaskReminderRecipient(
  recipientUserId: string,
  createdByUserId: string,
): void {
  if (recipientUserId !== createdByUserId) {
    throw new Error('Task reminders can only be created for yourself.');
  }
}

/** Default reminder time-of-day for day-based presets (local time). */
export const TASK_REMINDER_DEFAULT_HOUR = 9;

export function reminderPresetInHours(hours: number, from: Date = new Date()): Date {
  const d = new Date(from);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + hours);
  if (d.getTime() <= from.getTime()) {
    d.setHours(d.getHours() + 1);
  }
  return d;
}

export function reminderPresetCalendarDays(
  days: number,
  hour = TASK_REMINDER_DEFAULT_HOUR,
  from: Date = new Date(),
): Date {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  if (d.getTime() <= from.getTime()) {
    d.setDate(d.getDate() + 1);
    d.setHours(hour, 0, 0, 0);
  }
  return d;
}

export function reminderPresetBusinessDays(
  businessDays: number,
  hour = TASK_REMINDER_DEFAULT_HOUR,
  from: Date = new Date(),
): Date {
  let d = addBusinessDaysFromStart(from, Math.max(1, businessDays));
  d.setHours(hour, 0, 0, 0);
  if (d.getTime() <= from.getTime()) {
    d = addBusinessDaysFromStart(from, Math.max(1, businessDays) + 1);
    d.setHours(hour, 0, 0, 0);
  }
  return d;
}

export interface TaskReminderPreset {
  id: string;
  label: string;
  resolveAt: (from?: Date) => Date;
}

export const TASK_REMINDER_PRESETS: TaskReminderPreset[] = [
  { id: '1h', label: 'In 1 hour', resolveAt: (from) => reminderPresetInHours(1, from) },
  { id: '1d', label: 'Tomorrow 9 AM', resolveAt: (from) => reminderPresetCalendarDays(1, TASK_REMINDER_DEFAULT_HOUR, from) },
  { id: '1w', label: 'In 1 week', resolveAt: (from) => reminderPresetCalendarDays(7, TASK_REMINDER_DEFAULT_HOUR, from) },
  { id: '1bd', label: '1 business day', resolveAt: (from) => reminderPresetBusinessDays(1, TASK_REMINDER_DEFAULT_HOUR, from) },
  { id: '3bd', label: '3 business days', resolveAt: (from) => reminderPresetBusinessDays(3, TASK_REMINDER_DEFAULT_HOUR, from) },
  { id: '5bd', label: '5 business days', resolveAt: (from) => reminderPresetBusinessDays(5, TASK_REMINDER_DEFAULT_HOUR, from) },
];

export interface TaskReminderRecurringInterval {
  id: string;
  label: string;
  minutes: number;
}

export const TASK_REMINDER_RECURRING_INTERVALS: TaskReminderRecurringInterval[] = [
  { id: '1h', label: 'Every hour', minutes: 60 },
  { id: '4h', label: 'Every 4 hours', minutes: 240 },
  { id: '1d', label: 'Daily', minutes: 24 * 60 },
  { id: '2d', label: 'Every 2 days', minutes: 48 * 60 },
  { id: '1w', label: 'Weekly', minutes: 7 * 24 * 60 },
  { id: '2w', label: 'Every other week', minutes: 14 * 24 * 60 },
  { id: '1mo', label: 'Every month', minutes: 30 * 24 * 60 },
];

const RECURRING_MINUTES = new Set(TASK_REMINDER_RECURRING_INTERVALS.map((i) => i.minutes));

export function isAllowedRecurringIntervalMinutes(minutes: number): boolean {
  return RECURRING_MINUTES.has(minutes);
}

export function formatRecurringIntervalLabel(minutes: number): string {
  const match = TASK_REMINDER_RECURRING_INTERVALS.find((i) => i.minutes === minutes);
  if (match) return match.label;
  if (minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60);
    return days === 1 ? 'Daily' : `Every ${days} days`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? 'Every hour' : `Every ${hours} hours`;
  }
  return `Every ${minutes} minutes`;
}

export function computeNextRecurringRemindAt(
  currentRemindAt: Date,
  intervalMinutes: number,
  from: Date = new Date(),
): Date {
  let next = new Date(currentRemindAt.getTime() + intervalMinutes * 60 * 1000);
  while (next.getTime() <= from.getTime()) {
    next = new Date(next.getTime() + intervalMinutes * 60 * 1000);
  }
  return next;
}
